import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Card,
    Typography,
    Space,
    Button,
    message,
    Spin,
    Alert,
    Divider,
    Descriptions,
    Tag
} from 'antd';
import {
    CheckCircleOutlined,
    CloseCircleOutlined,
    InfoCircleOutlined,
    ClockCircleOutlined,
    UserOutlined
} from '@ant-design/icons';
import moment from 'moment';
import auctionService from '../../services/auctionService';

const { Title, Text, Paragraph } = Typography;

const AuctionInvitation = () => {
    const { auctionSlug, invitationCode } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [auction, setAuction] = useState(null);
    const [error, setError] = useState(null);
    const [responding, setResponding] = useState(false);

    useEffect(() => {
        loadAuctionDetails();
    }, [auctionSlug]);

    const loadAuctionDetails = async () => {
        try {
            setLoading(true);
            const response = await auctionService.getAuctionDetails(auctionSlug);
            setAuction(response.data);
            setError(null);
        } catch (err) {
            setError('Failed to load auction details. The invitation may be invalid or expired.');
        } finally {
            setLoading(false);
        }
    };

    const handleResponse = async (status) => {
        try {
            setResponding(true);
            await auctionService.respondToInvitation(auctionSlug, {
                invitation_code: invitationCode,
                status: status
            });
            
            message.success(`Invitation ${status} successfully`);
            navigate(`/auctions/${auctionSlug}`);
        } catch (err) {
            message.error(err.response?.data?.message || 'Failed to respond to invitation');
        } finally {
            setResponding(false);
        }
    };

    if (loading) {
        return (
            <div style={{ textAlign: 'center', padding: '50px' }}>
                <Spin size="large" />
                <Text style={{ display: 'block', marginTop: '20px' }}>Loading invitation details...</Text>
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ padding: '50px' }}>
                <Alert
                    message="Error"
                    description={error}
                    type="error"
                    showIcon
                />
            </div>
        );
    }

    return (
        <div style={{ maxWidth: '800px', margin: '0 auto', padding: '24px' }}>
            <Card>
                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                    <div style={{ textAlign: 'center' }}>
                        <Title level={2}>Auction Invitation</Title>
                        <Text type="secondary">You've been invited to participate in this auction</Text>
                    </div>

                    <Divider />

                    <Descriptions title="Auction Details" bordered>
                        <Descriptions.Item label="Title" span={3}>
                            {auction.title}
                        </Descriptions.Item>
                        <Descriptions.Item label="Description" span={3}>
                            {auction.description}
                        </Descriptions.Item>
                        <Descriptions.Item label="Start Time">
                            {moment(auction.start_time).format('MMM D, YYYY HH:mm')}
                        </Descriptions.Item>
                        <Descriptions.Item label="Status">
                            <Tag color={auction.status === 'active' ? 'success' : 'default'}>
                                {auction.status.toUpperCase()}
                            </Tag>
                        </Descriptions.Item>
                    </Descriptions>

                    <Divider />

                    <div style={{ textAlign: 'center' }}>
                        <Space size="large">
                            <Button
                                type="primary"
                                size="large"
                                icon={<CheckCircleOutlined />}
                                onClick={() => handleResponse('accepted')}
                                loading={responding}
                            >
                                Accept Invitation
                            </Button>
                            <Button
                                danger
                                size="large"
                                icon={<CloseCircleOutlined />}
                                onClick={() => handleResponse('declined')}
                                loading={responding}
                            >
                                Decline Invitation
                            </Button>
                        </Space>
                    </div>
                </Space>
            </Card>
        </div>
    );
};

export default AuctionInvitation; 